/** Payment links and e-signature on a quote or an order. */
import { useState } from "react";

import { ApiError } from "@/api/client";
import { downloadFile } from "@/api/commerce";
import { useEsignAction, useEsignRequests, usePaymentAction, usePaymentRequests } from "@/api/platform";
import { Badge } from "@/components/crm/Badge";
import { Icon } from "@/components/icons";
import { Alert, formatDate } from "@/components/ui";
import { money } from "@/lib/format";

type Doc = "quote" | "order";
const message = (e: unknown) =>
  e instanceof ApiError && e.problem.errors?.length ? e.problem.errors.map((x) => x.message).join("; ") : e instanceof Error ? e.message : "Something went wrong";

const PAY_TONE = { sent: "brand", paid: "ok", cancelled: "neutral" } as const;

export function PaymentPanel({ doc, id, currency, canEdit, open }: { doc: Doc; id: string; currency: string; canEdit: boolean; open: boolean }) {
  const requests = usePaymentRequests(doc === "order" ? { order_id: id } : { quote_id: id });
  const action = usePaymentAction();
  const [amount, setAmount] = useState("");
  const [percent, setPercent] = useState(doc === "quote" ? "30" : "");
  const [error, setError] = useState<string | null>(null);
  const send = () => {
    setError(null);
    const body: Record<string, string> = {};
    if (amount.trim()) body.amount = amount.trim();
    else if (doc === "quote" && percent.trim()) body.percent = percent.trim();
    void action.mutateAsync({ path: `/${doc}s/${id}/payment-requests`, body }).then(() => setAmount("")).catch((e: unknown) => setError(message(e)));
  };
  const list = requests.data ?? [];
  return (
    <section className="card space-y-3 p-5 text-sm" aria-label="Payments">
      <h2 className="flex items-center gap-2 font-semibold"><Icon name="tag" />Payments</h2>
      {list.length === 0 ? <p className="text-muted">No payment links yet.</p> : (
        <ul className="space-y-2">
          {list.map((r) => (
            <li key={r.id} className="rounded-md border border-line p-2.5" data-testid="payment-request">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium tabular-nums">{money(r.amount, r.currency)}</span>
                <Badge tone={PAY_TONE[r.status]}>{r.status === "sent" ? "Waiting" : r.status === "paid" ? "Paid" : "Cancelled"}</Badge>
              </div>
              <p className="text-xs text-muted">
                {r.status === "paid" ? `Paid ${r.paid_at ? formatDate(r.paid_at) : ""}${r.payment_ref ? ` · ${r.payment_ref}` : ""}` : `Sent ${formatDate(r.created_at)}${r.sent_to ? ` to ${r.sent_to}` : ""}`}
              </p>
              {r.status === "sent" && (
                <div className="mt-1.5 flex gap-2">
                  {r.url && <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => void navigator.clipboard?.writeText(r.url ?? "")}>Copy link</button>}
                  {canEdit && <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => { if (window.confirm("Cancel this payment link?")) void action.mutateAsync({ path: `/payment-requests/${r.id}/cancel` }); }}>Cancel</button>}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <Alert>{error}</Alert>}
      {canEdit && open && (
        <div className="space-y-2 border-t border-line pt-3">
          <div className="grid grid-cols-2 gap-2">
            {doc === "quote" && <div><label htmlFor="pay-pct" className="label">Advance %</label><input id="pay-pct" type="number" min={1} max={100} className="input" value={percent} onChange={(e) => setPercent(e.target.value)} /></div>}
            <div className={doc === "quote" ? "" : "col-span-2"}><label htmlFor="pay-amt" className="label">Amount ({currency})</label>
              <input id="pay-amt" inputMode="decimal" className="input" value={amount} placeholder={doc === "order" ? "Balance due" : "Or a fixed amount"} onChange={(e) => setAmount(e.target.value)} /></div>
          </div>
          <button type="button" className="btn-primary w-full" disabled={action.isPending} onClick={send}>{action.isPending ? "Creating link…" : "Send payment link"}</button>
          <p className="text-xs text-muted">The customer gets an email with a secure link (UPI, card or net banking). It is marked paid here as soon as they pay.</p>
        </div>
      )}
    </section>
  );
}

const SIGN_TONE: Record<string, "brand" | "ok" | "danger" | "neutral"> = { sent: "brand", completed: "ok", declined: "danger", expired: "neutral", recalled: "neutral", failed: "danger" };

export function EsignPanel({ doc, id, canEdit, open }: { doc: Doc; id: string; canEdit: boolean; open: boolean }) {
  const requests = useEsignRequests(doc === "order" ? { order_id: id } : { quote_id: id });
  const action = useEsignAction();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const send = () => {
    setError(null);
    void action.mutateAsync({ path: `/${doc}s/${id}/esign`, body: email.trim() ? { email: email.trim() } : {} })
      .then(() => setEmail("")).catch((e: unknown) => setError(message(e)));
  };
  const list = requests.data ?? [];
  const waiting = list.some((r) => r.status === "sent");
  return (
    <section className="card space-y-3 p-5 text-sm" aria-label="E-signature">
      <h2 className="flex items-center gap-2 font-semibold"><Icon name="check" />E-signature</h2>
      {list.length === 0 ? <p className="text-muted">Not sent for signature.</p> : (
        <ul className="space-y-2">
          {list.map((r) => (
            <li key={r.id} className="rounded-md border border-line p-2.5" data-testid="esign-request">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{r.recipient_name}</span>
                <Badge tone={SIGN_TONE[r.status] ?? "neutral"}>{r.status === "sent" ? "Waiting to sign" : r.status === "completed" ? "Signed" : r.status}</Badge>
              </div>
              <p className="text-xs text-muted">{r.status === "completed" && r.signed_at ? `Signed ${formatDate(r.signed_at)}` : `Sent ${formatDate(r.created_at)} to ${r.recipient_email}`}</p>
              {r.error && <p className="text-xs text-danger">{r.error}</p>}
              <div className="mt-1.5 flex gap-2">
                {r.status === "completed" && <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => void downloadFile(`/esign-requests/${r.id}/pdf`, `${r.title}-signed.pdf`)}><Icon name="download" />Signed PDF</button>}
                {r.status === "sent" && <button type="button" className="btn-ghost px-2 py-1 text-xs" disabled={action.isPending} onClick={() => void action.mutateAsync({ path: `/esign-requests/${r.id}/refresh` })}>Check now</button>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {error && <Alert>{error}</Alert>}
      {canEdit && open && !waiting && (
        <div className="space-y-2 border-t border-line pt-3">
          <div><label htmlFor="sign-email" className="label">Signer email</label>
            <input id="sign-email" type="email" className="input" value={email} placeholder={`The contact on the ${doc}`} onChange={(e) => setEmail(e.target.value)} /></div>
          <button type="button" className="btn-ghost w-full" disabled={action.isPending} onClick={send}>{action.isPending ? "Sending…" : "Send for e-signature"}</button>
        </div>
      )}
    </section>
  );
}
